import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon
} from '@heroicons/react/24/outline';
import { Controller } from 'react-hook-form';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { Button } from 'components/buttons';
import { MARKDOWN_FEATURES, toMarkdownString } from 'utils/markdown/markdown';
import InputLabel from '../InputLabel/InputLabel.jsx';
import InputError from '../InputError/InputError.jsx';
import { Skeleton } from '../../layout/loading';

const FEATURE_TOOLBAR = {
  [MARKDOWN_FEATURES.BOLD]: {
    label: 'Bold',
    icon: BoldIcon,
    isActive: (editor) => editor.isActive('bold'),
    run: (editor) => editor.chain().focus().toggleBold().run()
  },
  [MARKDOWN_FEATURES.ITALIC]: {
    label: 'Italic',
    icon: ItalicIcon,
    isActive: (editor) => editor.isActive('italic'),
    run: (editor) => editor.chain().focus().toggleItalic().run()
  },
  [MARKDOWN_FEATURES.BULLET_LIST]: {
    label: 'Bulleted list',
    icon: ListBulletIcon,
    isActive: (editor) => editor.isActive('bulletList'),
    run: (editor) => editor.chain().focus().toggleBulletList().run()
  },
  [MARKDOWN_FEATURES.ORDERED_LIST]: {
    label: 'Numbered list',
    icon: NumberedListIcon,
    isActive: (editor) => editor.isActive('orderedList'),
    run: (editor) => editor.chain().focus().toggleOrderedList().run()
  }
};

const TOOLBAR_FEATURE_ORDER = [
  MARKDOWN_FEATURES.BOLD,
  MARKDOWN_FEATURES.ITALIC,
  MARKDOWN_FEATURES.BULLET_LIST,
  MARKDOWN_FEATURES.ORDERED_LIST
];

function buildStarterKitOptions(features) {
  const enabled = new Set(features);

  return {
    heading: false,
    blockquote: false,
    codeBlock: false,
    code: false,
    horizontalRule: false,
    strike: false,
    link: false,
    bold: enabled.has(MARKDOWN_FEATURES.BOLD) ? undefined : false,
    italic: enabled.has(MARKDOWN_FEATURES.ITALIC) ? undefined : false,
    bulletList: enabled.has(MARKDOWN_FEATURES.BULLET_LIST) ? undefined : false,
    orderedList: enabled.has(MARKDOWN_FEATURES.ORDERED_LIST) ? undefined : false,
    listItem: enabled.has(MARKDOWN_FEATURES.BULLET_LIST) || enabled.has(MARKDOWN_FEATURES.ORDERED_LIST)
      ? undefined
      : false
  };
}

function ToolbarButton({ active = false, disabled, icon, label, onClick }) {
  return (
    <Button
      aria-label={label}
      title={label}
      icon={icon}
      disabled={disabled}
      isPrimary={active}
      className="min-w-10 px-2.5 py-2"
      onMouseDown={(event) => {
        event.preventDefault();

        if (!disabled) {
          onClick();
        }
      }}
    />
  );
}

function EditorToolbar({ editor, features, disabled }) {
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!editor) return undefined;

    const update = () => forceRender((tick) => tick + 1);

    editor.on('selectionUpdate', update);
    editor.on('transaction', update);

    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {TOOLBAR_FEATURE_ORDER
        .filter((feature) => features.includes(feature))
        .map((feature) => {
          const config = FEATURE_TOOLBAR[feature];

          return (
            <ToolbarButton
              key={feature}
              active={config.isActive(editor)}
              disabled={disabled}
              icon={config.icon}
              label={config.label}
              onClick={() => config.run(editor)}
            />
          );
        })}
    </div>
  );
}

function MarkdownEditor({
  disabled,
  features,
  labelText,
  name,
  onBlur,
  onChange,
  placeholder,
  rows,
  value
}) {
  const extensions = useMemo(() => [
    StarterKit.configure(buildStarterKitOptions(features)),
    Markdown.configure({ html: false, linkify: false, breaks: true })
  ], [features]);

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': labelText,
        'aria-multiline': 'true',
        id: `${name}-write`,
        spellcheck: 'true',
        class: classNames(
          'prose-invert max-w-none cursor-text text-sm leading-6 text-white outline-none',
          '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5',
          { 'cursor-not-allowed opacity-50': disabled }
        )
      }
    },
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
    },
    onUpdate: ({ editor: instance }) => {
      const next = instance.storage.markdown.getMarkdown();

      if (next !== value) {
        onChange(next);
      }
    }
  }, [extensions]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;

    const current = editor.storage.markdown.getMarkdown();

    if (current !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  const minHeight = `${Math.max(rows, 4) * 1.5}rem`;

  return (
    <div>
      <EditorToolbar editor={editor} features={features} disabled={disabled} />
      <div className="mt-3 rounded-lg bg-gray-950/40 px-3 py-2.5">
        <div className="relative">
          <EditorContent editor={editor} style={{ minHeight }} />
          {editor && editor.isEmpty && placeholder ? (
            <span className="pointer-events-none absolute left-0 top-0 select-none text-sm leading-6 text-gray-500">
              {placeholder}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MarkdownEditorInput({
  field,
  fieldState,
  className,
  disabled,
  features,
  labelText,
  labelRightElement,
  maxLength,
  name,
  placeholder,
  rows,
  helperText
}) {
  const value = toMarkdownString(field.value);
  const validationMessage = fieldState.error?.message;

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-4">
        <InputLabel labelRightElement={labelRightElement}>
          {labelText}
        </InputLabel>
        <span className="text-xs text-gray-400" id={`${name}-description`}>
          {value.length}/{maxLength} characters
        </span>
      </div>

      <div className="mt-2 overflow-hidden rounded-xl border border-white/10 bg-white/5">
        <div className="p-3">
          <MarkdownEditor
            disabled={disabled}
            features={features}
            labelText={labelText}
            name={name}
            onBlur={field.onBlur}
            onChange={field.onChange}
            placeholder={placeholder}
            rows={rows}
            value={value}
          />
          {helperText && (
            <p className="mt-3 text-xs text-gray-400">{helperText}</p>
          )}
          {validationMessage && <InputError>{validationMessage}</InputError>}
        </div>
      </div>
    </div>
  );
}

export default function MarkdownEditorField({
  control,
  name,
  features,
  maxLength,
  labelText = '',
  labelRightElement = null,
  placeholder = '',
  helperText = '',
  loading = false,
  rows = 6,
  className = '',
  disabled = false,
  rules = {}
}) {
  if (loading) {
    return (
      <div className={className}>
        {labelText && <Skeleton variant="text" className="mb-2 w-24" />}
        <Skeleton variant="rectangular" className="w-full rounded-md" style={{ height: `${Math.max(rows, 4) * 1.5}rem` }} />
      </div>
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <MarkdownEditorInput
          field={field}
          fieldState={fieldState}
          className={className}
          disabled={disabled}
          features={features}
          labelText={labelText}
          labelRightElement={labelRightElement}
          maxLength={maxLength}
          name={name}
          placeholder={placeholder}
          rows={rows}
          helperText={helperText}
        />
      )}
    />
  );
}
